import React, { useEffect, useState } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Table, Badge, Alert, Spinner, Input, Button,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getAdminUsers } from "../../services/adminService";

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

/** How long ago, in the coarse terms this page is actually read at. */
const ago = (v) => {
  if (!v) return "never";
  const days = (Date.now() - new Date(v).getTime()) / 86400000;
  if (days < 1 / 24) return "just now";
  if (days < 1) return `${Math.round(days * 24)}h ago`;
  if (days < 30) return `${Math.round(days)}d ago`;
  return `${Math.round(days / 30)}mo ago`;
};

const SubscriptionCell = ({ user }) => {
  if (user.is_admin) return <Badge color="dark">Admin</Badge>;
  if (!user.subscription_status) return <Badge color="light" className="text-body">Free · tier 0</Badge>;
  return (
    <>
      <Badge color={user.subscription_active ? "success" : "secondary"} className="me-1">
        {user.subscription_status} · tier {user.tier ?? 0}
      </Badge>
      {user.all_access ? (
        <Badge color="info">all symbols</Badge>
      ) : (
        user.entitled_symbols.map((s) => (
          <Badge color="info" key={s} className="me-1">{s}</Badge>
        ))
      )}
    </>
  );
};

const Stat = ({ label, value, color = "primary" }) => (
  <Col>
    <Card className="mb-3">
      <CardBody className="text-center py-3">
        <h4 className={`mb-1 text-${color}`}>{value}</h4>
        <span className="text-muted fs-13">{label}</span>
      </CardBody>
    </Card>
  </Col>
);

/**
 * Who is using the app: last sign-in, plan, and how much research each member
 * has generated. Research generation spends paid market data, so "who can
 * create" and "who is creating" belong on the same screen.
 */
const UserActivity = () => {
  document.title = "User Activity | ForecastNest";

  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminUsers();
      setUsers(res.data || []);
      setSummary(res.summary || null);
    } catch (e) {
      setError(typeof e === "string" ? e : "Could not load user activity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const term = filter.trim().toLowerCase();
  const shown = term
    ? users.filter((u) =>
        [u.email, u.username, u.display_name, u.account_name]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(term)))
    : users;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="User Activity" pageTitle="System Admin" />

        {error && <Alert color="danger">{error}</Alert>}

        {summary && (
          <Row>
            <Stat label="Members" value={summary.total} />
            <Stat label="Signed in (7d)" value={summary.active_7d} color="info" />
            <Stat label="Subscribed" value={summary.subscribed} color="success" />
            <Stat label="Created research" value={summary.with_research} color="warning" />
            <Stat label="Admins" value={summary.admins} color="dark" />
          </Row>
        )}

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="card-title mb-0">Members</h5>
            <div className="d-flex gap-2">
              <Input bsSize="sm" style={{ width: 220 }} placeholder="Filter by name or email"
                     value={filter} onChange={(e) => setFilter(e.target.value)} />
              <Button size="sm" color="light" onClick={load} disabled={loading}>
                <i className="ri-refresh-line align-bottom me-1" />Refresh
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-4"><Spinner /></div>
            ) : shown.length === 0 ? (
              <p className="text-muted text-center py-4 mb-0">No members match that filter.</p>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Member</th>
                      <th>Email</th>
                      <th>Sign-in</th>
                      <th>Last seen</th>
                      <th>Joined</th>
                      <th>Plan</th>
                      <th>Research</th>
                      <th>Last research</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <span className="fw-medium">
                            {u.display_name || u.account_name || u.username}
                          </span>
                          {u.display_name && u.account_name && u.display_name !== u.account_name && (
                            <span className="text-muted fs-12 d-block">account: {u.account_name}</span>
                          )}
                        </td>
                        <td className="text-muted">{u.email}</td>
                        <td>
                          <Badge color={u.provider === "google" ? "danger" : "secondary"} className="text-uppercase">
                            {u.provider}
                          </Badge>
                        </td>
                        <td>
                          {ago(u.last_login_at)}
                          <span className="text-muted fs-12 d-block">{fmtDate(u.last_login_at)}</span>
                        </td>
                        <td className="text-muted">{fmtDate(u.created_at)}</td>
                        <td><SubscriptionCell user={u} /></td>
                        <td>
                          {u.research_count > 0
                            ? <Badge color="primary">{u.research_count}</Badge>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-muted">{ago(u.last_research_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default UserActivity;
